import { Component, HostListener } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { EventHandlerService } from 'src/app/data/services/event-handler.service';
import { LocalStorageService } from 'src/app/data/services/local-storage.service';
import { PJ_CONSTANTS } from 'src/app/shared/constants/pessoa-juridica.constants';
import { VoiceContactStatusAgent } from 'src/app/shared/enums/voice-contact-status-agent.enum';
import { PessoaJuridicaChanelProvider } from 'src/app/shared/models/pessoa-juridica/pessoa-juridica-chanel-provider.model';
import { CriacaoTicketPessoaJuridica } from 'src/app/shared/models/pessoa-juridica/pessoa-juridica-criacao-ticket.model';
import { modalUraAutenticadora } from 'src/app/shared/services/modal-ura-autenticadora.services';
import { LoggerService } from '../../core/services/logger.service';
import { LoginService } from '../../core/services/login.service';
import { NiceSdkAuthenticationService } from '../../data/CXOne/sdk-auth/nice-sdk-authentication.service';
import { DynamicsPessoaJuridica } from '../../data/Dynamics/dynamics-pessoa-juridica.service';
import { GenericModalService } from 'src/app/shared/services/generic-modal.service';
import { ContactCenterPjService } from 'src/app/data/CXOne/services/contact-center-pj.service';
import { AgentStateEventDTO } from '../pessoa-fisica/domain/ContactCenter/AgentStateEventDTO';
import { VoiceEventDTO } from 'src/app/data/CXOne/DataTransferObjects/VoiceEventDTO';
import {
  DigitalEventDTO,
  DigitalSessionDirection,
  DigitalSessionEndReason,
  DigitalSessionEventType,
} from 'src/app/data/CXOne/DataTransferObjects/DigitalEventDTO';
import { environment } from 'src/environments/environment';
import { isAniRedirect } from 'src/app/utils/cxone-utils';
import { TicketCreationRequestControlService } from 'src/app/shared/services/ticket-creation-request-control.service';
import { CIFDatadogService } from 'src/app/shared/services/cif-datadog';
import { MediaType } from 'src/app/utils/media-type';
import { LocalStorageUtils } from 'src/app/data/CXOne/util/LocalStorageUtils';

@Component({
  selector: 'app-pessoa-juridica',
  standalone: true,
  imports: [],
  templateUrl: './pessoa-juridica.component.html',
  styleUrl: './pessoa-juridica.component.css',
})
export class PessoaJuridicaComponent {
  private agentNextState = '';
  private sessionId = '';
  private timeModalUra: number = PJ_CONSTANTS.TIME_MODAL_URA;
  private destroy$ = new Subject<void>();
  private channelProviderVariables!: PessoaJuridicaChanelProvider;

  constructor(
    private niceSdkAuthService: NiceSdkAuthenticationService,
    private contactCenterService: ContactCenterPjService,
    private logger: LoggerService,
    private dynamics: DynamicsPessoaJuridica,
    private loginService: LoginService,
    private localStorage: LocalStorageService,
    private eventHandler: EventHandlerService,
    private modalUraAutenticadora: modalUraAutenticadora,
    private modalGeneric: GenericModalService,
    private ticketCreationRequestControlService: TicketCreationRequestControlService,
    private cifDatadogService: CIFDatadogService,
    private localStorageUtils: LocalStorageUtils,
  ) {}

  /**
   * Listener para verificar se a aba do navegador está sendo fechada
   * @param event BeforeUnloadEvent
   */
  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent): void {
    this.logger.warn(`PJ - Fechando/Atualizando aba do navegador!`);
    event.preventDefault();
    event.returnValue = '';
  }

  async ngOnInit() {
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    window.addEventListener('unload', this.handleBeforeUnload);
    this.logger.info('PJ - inicializado');
    const centralId = this.localStorageUtils.safeGetItem('centralId') || 'pj';
    const authServiceType =
      this.localStorageUtils.safeGetItem('authServiceType') || 'nice';
    const authMode =
      this.localStorageUtils.safeGetItem('display_mode') || 'popup';

    this.inicializarAuthStatusListener();

    try {
      const isAuthenticated = await this.loginService.login(
        authServiceType,
        centralId,
        true,
        authMode,
      );

      if (isAuthenticated) {
        this.logger.info('PJ - Usuario autenticado');
        await this.inicializarEventosCXone(); // Chama na primeira autenticação também
        this.addEvents();
        const channelProvider = await this.dynamics.loadTemplateValues();
        const invalidFields =
          this.getInvalidChannelProviderFields(channelProvider);
        if (invalidFields.length > 0) {
          this.logger.error(
            `PJ - Erro ao carregar as variáveis do canal de atendimento do Dynamics. Verifique os campos: ${JSON.stringify(invalidFields)}.`,
          );
          this.modalGeneric.createModal({
            title: 'Erro ao inicializar.',
            description:
              'Erro ao carregar as variáveis do canal de atendimento do Dynamics. Favor entrar em contato com o suporte.',
            allowCloseModal: false,
          });
          return;
        }

        this.channelProviderVariables =
          channelProvider as PessoaJuridicaChanelProvider;

        // abre uma aba com a listagem de tickets associados a um usuário
        const inputSessionPadrao = {
          templateName: this.channelProviderVariables.TEMPLATE_SESSAO_PADRAO,
          templateParameters: {},
          context: {
            sessaobetta: true,
          },
        };
        this.sessionId = await this.dynamics.cifCreateSession(
          inputSessionPadrao,
          null,
          '',
        );
        this.logger.info('PJ - Sessão criada com ID:', this.sessionId);
      }
    } catch (error) {
      if (error instanceof Error && error.message) {
        this.logger.error(error);
      } else {
        this.logger.error('PJ - Erro ao iniciar o componente: ', error);
      }
    }
  }

  private addEvents() {
    this.contactCenterService.onAgentStatusEvent$.subscribe(
      (agentStateEvent: AgentStateEventDTO) => {
        this.logger.info(
          `PJ (ACD) - Agent State | currentState: ${agentStateEvent.agentCurrentState} | nextState: ${agentStateEvent.agentNextState}`,
        );
        this.agentNextState = agentStateEvent.agentNextState;
        this.openAndCloseModalUra();
      },
    );
    this.contactCenterService.onVoiceCallEvent$.subscribe(
      async (voiceContact) => {
        const status = voiceContact.Status || '';
        const contactId = voiceContact.ContactId || '';
        const masterId = voiceContact.MasterId || '';
        const contact = voiceContact.isInboundCall ? 'Inbound' : 'Outbound';
        const callType = voiceContact.CallType || '';
        const contatoEvento = { id: contactId, status: status };
        const screenPopUrl = voiceContact.AttachData || '';
        const eventoProcessado = this.eventHandler.isProcessed(contatoEvento);
        const isEmpty = (obj: object) => Object.keys(obj).length === 0;
        const logStringAgent = `Contato: ${contact} | Status: ${status} | ContactID: ${contactId} | callType: ${callType} | eventoProcessado: ${eventoProcessado} `;

        if (!eventoProcessado) {
          this.logger.info(`PJ (ACD - Evento Geral - ${logStringAgent})`);
        }

        this.logger.info('Evento recebido: ', voiceContact);

        //LIGAÇÃO OUTBOUND
        if (
          status === VoiceContactStatusAgent.ACTIVE &&
          contact == 'Outbound'
        ) {
          if (isAniRedirect(voiceContact.Ani)) return;

          this.logger.info(
            `PJ (ACD - ${logStringAgent}) | Iniciando atendimento outbound.`,
          );
          let payload: CriacaoTicketPessoaJuridica;
          let useScreenPopUrl = false;
          let screenPopUrlObj: PessoaJuridicaChanelProvider;
          if (isEmpty(screenPopUrl)) {
            this.logger.info(
              `PJ (ACD - ${logStringAgent}) | screenPopUrl: ${screenPopUrl}. Attachdata vazio ou não encontrado no evento recebido.`,
            );
            payload = this.payloadVozOutbound(
              voiceContact.Dnis || '',
              contactId,
            );
            useScreenPopUrl = false;
            screenPopUrlObj = {} as PessoaJuridicaChanelProvider;
          } else {
            this.logger.info(
              `PJ (ACD - ${logStringAgent}) | screenPopUrl: ${screenPopUrl}. Voltou da ura autenticadora.`,
            );
            screenPopUrlObj = this.screenPopUrlLoad(voiceContact);
            payload = this.payloadVozInbound(screenPopUrlObj, contactId);
            useScreenPopUrl = true;
          }
          this.logger.info(
            `PJ (ACD - ${logStringAgent}) | payload: ${JSON.stringify(payload)}`,
          );
          this.eventHandler.add(contatoEvento);
          await this.iniciaAtendimento(
            contactId,
            payload,
            '',
            '',
            logStringAgent,
            useScreenPopUrl,
            screenPopUrlObj,
            'voice',
          );
        }

        //LIGAÇÃO INBOUND
        if (status === VoiceContactStatusAgent.ACTIVE && contact == 'Inbound') {
          this.logger.info(
            `PJ (ACD - ${logStringAgent}) | Iniciando atendimento inbound.`,
          );
          if (screenPopUrl === '') {
            this.logger.error(
              `PJ (ACD - ${logStringAgent}) | screenPopUrl: ${screenPopUrl}. Attachdata vazio ou não encontrado no evento recebido.`,
            );
            return;
          }
          this.logger.info(
            `PJ (ACD - ${logStringAgent}) | screenPopUrl: ${screenPopUrl}.`,
          );
          const screenPopUrlObj = this.screenPopUrlLoad(voiceContact);
          const payload = this.payloadVozInbound(screenPopUrlObj, contactId);
          this.logger.info(
            `PJ (ACD - ${logStringAgent}) | payload: ${JSON.stringify(payload)}`,
          );
          this.eventHandler.add(contatoEvento);
          await this.iniciaAtendimento(
            contactId,
            payload,
            '',
            '',
            logStringAgent,
            true,
            screenPopUrlObj,
            'voice',
          );
        }

        if (status === VoiceContactStatusAgent.DISCONNECTED) {
          this.openAndCloseModalUra();
          this.logger.info(
            `PJ (ACD - ${logStringAgent}) - Contato desconectado ou transferido`,
          );
          this.eventHandler.remove(contatoEvento);
          await this.encerraAtendimento(
            contactId,
            this.sessionId,
            voiceContact.InteractionId,
            'voice',
          );
        }
      },
    );

    this.contactCenterService.onDigitalSessionEvent$.subscribe(
      (digitalSessionEvent) => {
        this.logger.info('Evento de digital recebido: ', digitalSessionEvent);

        switch (digitalSessionEvent.SessionEventType) {
          case DigitalSessionEventType.Assigned: {
            if (
              digitalSessionEvent.SessionDirection ===
              DigitalSessionDirection.Inbound
            ) {
              this.OnDigitalSessionReceived(digitalSessionEvent);
            }
            break;
          }
          case DigitalSessionEventType.Unassigned: {
            if (
              digitalSessionEvent.SessionDirection ===
              DigitalSessionDirection.Inbound
            ) {
              this.OnDigitalSessionEnded(digitalSessionEvent);
            }
            break;
          }
        }
      },
    );

    this.contactCenterService.onFocusEvent$.subscribe((FocusEvent) => {
      this.logger.info(
        'Solicitação de foco recebida: ',
        JSON.stringify(FocusEvent),
      );

      try {
        this.localStorageUtils.safeSetItem('interactionFocused', FocusEvent.Id);
      } catch {
        /* empty */
      }
      this.dynamics.ChangeSessionFocus(FocusEvent.Id);
    });
  }

  private async OnDigitalSessionReceived(digitalSessionEvent: DigitalEventDTO) {
    try {
      this.logger.info(`PJ (DIGITAL) - evento recebido: `, digitalSessionEvent);
      const contactId = digitalSessionEvent.ContactId;

      const ticketAlreadyHasRequest =
        this.ticketCreationRequestControlService.checkTicketRequest(contactId);
      if (ticketAlreadyHasRequest) {
        this.logger.info(
          `Já existe uma solicitação de ticket para o evento digital: ${digitalSessionEvent.ContactId}`,
        );
        this.cifDatadogService.openTicket.request.duplicated({
          masterId: digitalSessionEvent.ContactId,
          attachData: digitalSessionEvent.AttachData,
          mediaType: digitalSessionEvent.mediaType,
        });
        return;
      }
      this.ticketCreationRequestControlService.saveTicketRequest(contactId);
      this.ticketCreationRequestControlService.clearUpLastTicketCreationRequest();

      const attachdata = digitalSessionEvent.AttachData;

      const sessionId = await this.dynamics.openTicket(
        attachdata,
        digitalSessionEvent.ContactId,
        false,
        digitalSessionEvent.mediaType,
      );
      this.localStorage.addSessaoAtendimento({ contactId, sessionId });
    } catch (error) {
      this.logger.error('- Erro ao processar contato de digital: ', error);
    }
  }

  private async OnDigitalSessionEnded(digitalSessionEvent: DigitalEventDTO) {
    if (
      digitalSessionEvent.SessionEndReason == DigitalSessionEndReason.Closed
    ) {
      await this.dynamics.endTicket(
        digitalSessionEvent.ContactId,
        this.sessionId,
      );
    }
  }

  /**
   * Método para inicializar o listener de status de autenticação
   * Escuta mudanças no status de autenticação e reinicializa eventos CXone se necessário
   */
  private inicializarAuthStatusListener() {
    this.niceSdkAuthService.authStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe((status) => {
        this.logger.info('PJ - Status de autenticação recebido:', status);
        if (status === 'AUTHENTICATED') {
          this.logger.info(
            'PJ - Reautenticado, reinicializando eventos CXone...',
          );
          this.inicializarEventosCXone();
        }
        if (
          status === 'NOT_AUTHENTICATED' ||
          status === 'AUTHENTICATION_FAILED'
        ) {
          this.logger.warn(
            'PJ - Usuário deslogado ou token expirado, limpando eventos...',
          );
        }
      });
  }

  /**
   * Método para inicializar eventos CXone
   */
  private async inicializarEventosCXone() {
    this.logger.info('PJ - Inicializando eventos CXone...');
    await this.contactCenterService.initializer();
    await this.dynamics.initialize(environment.cif_versao2_pj);
    this.logger.info('PJ - Eventos CXone inicializados.');
  }

  /**
   * Método para obter os campos inválidos do provedor de canal
   * @param obj PessoaJuridicaChanelProvider | null - Objeto a ser validado
   * @returns string[] - Lista de campos inválidos
   */
  private getInvalidChannelProviderFields(
    obj: PessoaJuridicaChanelProvider | null,
  ): string[] {
    if (!obj) return Object.keys({} as PessoaJuridicaChanelProvider);
    return Object.entries(obj)
      .filter(([, value]) => typeof value !== 'string' || value.trim() === '')
      .map(([key]) => key);
  }

  /**
   * Método para abrir e fechar o modal da URA autenticadora
   * Abre o modal se o agente estiver na fila "Autenticadora"
   * Fecha o modal se o agente sair da fila "Autenticadora"
   */
  private openAndCloseModalUra() {
    if (this.agentNextState === 'Autenticadora') {
      this.modalUraAutenticadora.showModalWithTimer(
        PJ_CONSTANTS.MESSAGE_URA,
        this.timeModalUra,
      );
    } else {
      this.modalUraAutenticadora.hideModal();
    }
  }

  /**
   * Método para montar o payload de saída para ligações outbound
   * @param voiceContact CXoneVoiceContact - Contato de voz
   * @param contactId string - ID do contato
   * @returns CriacaoTicketPessoaJuridica - Payload para criação do ticket
   */
  private payloadVozOutbound(phoneNumber: string, contactId: string) {
    let uJsonString = this.channelProviderVariables?.ATTACHDATA_U_JSON;
    if (!uJsonString || uJsonString.trim() === '') {
      uJsonString = '{}'; // valor padrão vazio
    }
    if (uJsonString.includes("'")) {
      uJsonString = uJsonString.replace(/'/g, '"');
    }

    let u_jsonAtualizado: any;
    try {
      u_jsonAtualizado = JSON.parse(uJsonString);
    } catch (error) {
      this.logger.error(
        'PJ - Erro ao converter o attachdata_u_json do channel provider em json:',
        error,
      );
      u_jsonAtualizado = {};
    }
    u_jsonAtualizado.U_NUMA = phoneNumber;
    const payload: CriacaoTicketPessoaJuridica = {
      itau_name: this.channelProviderVariables?.ITAU_NAME,
      itau_u_nomeprojeto: this.channelProviderVariables?.ITAU_U_NOMEPROJETO,
      itau_mst_attachdata: JSON.stringify(u_jsonAtualizado),
      'itau_u_lk_projeto@odata.bind': this.channelProviderVariables?.ITAU_U_LK,
      itau_st_id_conexao: contactId,
    };
    return payload;
  }

  /**
   * Método para montar o payload de entrada para ligações inbound
   * @param voiceContact CXoneVoiceContact - Contato de voz
   * @param contactId string - ID do contato
   * @returns CriacaoTicketPessoaJuridica - Payload para criação do ticket
   */
  private payloadVozInbound(
    screenPopUrlObj: PessoaJuridicaChanelProvider,
    contactId: string,
  ) {
    const payload: CriacaoTicketPessoaJuridica = {
      itau_name: screenPopUrlObj.ITAU_NAME,
      itau_u_nomeprojeto: screenPopUrlObj.ITAU_U_NOMEPROJETO,
      itau_mst_attachdata: screenPopUrlObj.ATTACHDATA_U_JSON,
      'itau_u_lk_projeto@odata.bind': screenPopUrlObj.ITAU_U_LK,
      itau_st_id_conexao: contactId,
    };
    return payload;
  }

  /**
   * Método para carregar os dados do URL do screen pop
   * @param voiceContact CXoneVoiceContact - Contato de voz
   * @returns PessoaJuridicaChanelProvider - Objeto com os dados do canal
   */
  private screenPopUrlLoad(
    voiceContact: VoiceEventDTO,
  ): PessoaJuridicaChanelProvider {
    const attachdata = voiceContact.AttachData;
    this.timeModalUra = attachdata.timeout_ura
      ? parseInt(attachdata.timeout_ura)
      : this.timeModalUra;
    const screenPopUrl: PessoaJuridicaChanelProvider = {
      ITAU_NAME: attachdata.ITAU_NAME || '',
      ITAU_U_NOMEPROJETO: attachdata.ITAU_U_NOMEPROJETO || '',
      ITAU_U_LK: attachdata.ITAU_U_LK || '',
      TEMPLATE_CREATE_SESSION: attachdata.TEMPLATECREATESESSION || '',
      TEMPLATE_SESSAO_PADRAO: attachdata.TEMPLATE_SESSAO_PADRAO || '',
      TABLE_NAME: attachdata.TABLE_NAME || '',
      RETRIEVE_RECORD_TABLE: attachdata.RETRIEVERECORDTABLE || '',
      RETRIEVE_RECORD_COL: attachdata.RETRIEVERECORDCOL || '',
      ATTACHDATA_U_JSON: attachdata.U_JSON || '',
    };
    return screenPopUrl;
  }

  /**
   * Método para iniciar o atendimento, criando o ticket e a sessão no Dynamics
   * @param contactId string - ID do contato
   * @param payload CriacaoTicketPessoaJuridica - Payload para criação do ticket
   * @param interactionId string - ID da interação
   * @param caseId string - ID do caso
   * @param logStringAgent string - String de log do agente
   * @param useScreenPopUrl boolean - Indica se deve usar o screenPopUrl
   * @param screenPopUrlObj PessoaJuridicaChanelProvider - Objeto com os dados do canal
   */
  private async iniciaAtendimento(
    contactId: string,
    payload: CriacaoTicketPessoaJuridica,
    interactionId: string,
    caseId: string,
    logStringAgent: string,
    useScreenPopUrl: boolean,
    screenPopUrlObj: PessoaJuridicaChanelProvider | undefined,
    mediaType: MediaType,
  ) {
    try {
      this.logger.info(`PJ (${logStringAgent}) - Iniciando criação do ticket.`);
      const provider =
        useScreenPopUrl && screenPopUrlObj
          ? screenPopUrlObj
          : this.channelProviderVariables;
      const providerInfo = useScreenPopUrl
        ? 'screenPopUrl'
        : 'Template do Channel Provider';
      this.logger.info(
        `PJ (${logStringAgent}) - Usando provedor: ${providerInfo} | Valores : ${JSON.stringify(provider)}`,
      );

      const result = await this.dynamics.cifCreateTicket(
        provider.TABLE_NAME,
        payload,
        provider.RETRIEVE_RECORD_TABLE,
        provider.RETRIEVE_RECORD_COL,
        PJ_CONSTANTS.RECALL_LIMIT,
        PJ_CONSTANTS.RECALL_DELAY_MS,
        contactId,
        mediaType,
      );
      this.logger.info(
        `PJ (${logStringAgent}) - Ticket gerado com sucesso: `,
        JSON.stringify(result),
      );

      if (!result || !result.id_interacao || !result.id_ticket) {
        throw new Error(`PJ ${logStringAgent} - Erro ao criar ticket`, {
          cause: result,
        });
      }

      const templateAtendimento = {
        templateName: provider.TEMPLATE_CREATE_SESSION,
        templateParameters: {
          id_interacao: result.id_interacao,
          id_ticket: result.id_ticket,
          oq2_cliente_on: true,
        },
        isFocused: true,
      };
      const sessionId = await this.dynamics.cifCreateSession(
        templateAtendimento,
        mediaType,
        contactId,
      );
      this.localStorage.addSessaoAtendimento({ contactId, sessionId });
      this.logger.info(
        `PJ (${logStringAgent}) - Sessão criada ${contactId} = ${sessionId}`,
      );
    } catch (error) {
      this.eventHandler.remove({ id: contactId, status: '' });
      throw error;
    }
  }

  /**
   * Método para encerrar o atendimento, removendo a sessão do localStorage
   * @param contactId
   * @param sessionId
   * @returns
   */
  private async encerraAtendimento(
    contactId: string,
    sessionId: string,
    interactionId: string,
    mediaType: MediaType,
  ) {
    try {
      this.logger.info('PJ - Contato desconectado');
      const sessaoAtiva = this.localStorage.getSessaoAtendimento({
        contactId,
        sessionId,
      });
      if (!sessaoAtiva.sessionId) {
        this.logger.warn(
          'PJ - sessionId não encontrado no localStorage, não é possível encerrar',
          contactId,
        );
        return;
      }
      this.localStorage.deleteSessaoAtendimento({ contactId, sessionId });
      await this.dynamics.cifCloseSession(
        sessaoAtiva.sessionId,
        contactId,
        interactionId,
        mediaType,
      );
      //localStorage.removeItem(sessionId);
      this.logger.info(`PJ - Sessão encerrada cif:`, sessaoAtiva);
    } catch (error) {
      this.eventHandler.remove({ id: contactId, status: '' });
      throw error;
    }
  }
}
